import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { BeatLoader } from 'react-spinners'
import { toast } from 'react-toastify'
import { Card, CardBody, Col, Row } from 'reactstrap'
import AddMinutes from '../../components/Common/Modals/AddMinutes'
import { confirm } from '../../components/Common/Modals/ConfirmService'
import PageTitle from '../../components/Common/PageTitle/PageTitle'
import Stepper from '../../components/Common/Stepper'
import QuillReact from '../../components/QuillTextarea/QuillReact'

import organizationService from '../../services/organizationService'
import plansAndBundlesService from '../../services/plansAndBundlesService'

import paymentService from '../../services/paymentService'
import PlanPayment from './Payment'
import Loader from '../../components/Loader/Loader'

import 'react-quill/dist/quill.snow.css'
import styles from './plan.module.scss'
import { PLAN_TYPES } from '../../constants'
import toastService from '../../services/toastService'
import sessionService from '../../services/sessionService'
import {
  getBillingDate,
  getNextRenewalDate,
  getRecurringBundleTotals,
  isMonthlyBundle,
  isAutoBillingOn,
  isStripeSubscription,
  isSubscriptionActive,
} from '../../utils/subscriptionUtils'

const options = {
  hideProgressBar: true,
}

const Plan = () => {
  const [state, setState] = useState({
    plans: [],
    organization: {},
    showModalMinut: false,
    showModalPayment: false,
    error: null,
    activeClass: '',
    loading: true,
    subscribe: false,
    planId: '',
  })
  const [addonExtensions, setAddonExtensions] = useState(0)
  const [addonGroups, setAddonGroups] = useState(0)
  const [addonSaving, setAddonSaving] = useState(false)
  const [useTrial, setUseTrial] = useState(false)
  const [bundleSaving, setBundleSaving] = useState(false)

  const { organizationId } = useParams()
  const navigate = useNavigate()

  // The free trial can only be offered if the user has not used it before.
  const user = sessionService.getUser()
  const trialAvailable = !user?.usedTrial

  useEffect(() => {
    loadOrganizationById()
    loadPlans()
  }, [])

  const loadOrganizationById = () => {
    organizationService
      .getById(organizationId)
      .then((result) => {
        // Groups (sub-organizations) have no plan of their own — all limits are
        // inherited from the parent organization. Redirect them to extensions.
        if (result?.parentId) {
          navigate('/dashboard/extensions/' + organizationId, { replace: true })
          return
        }
        setState((prev) => ({
          ...prev,
          organization: result,
          name: result.name,
          activeClass: result.subscription ? result.subscription.planId : null,
          subscribe: result?.subscription?.neverExpires
            ? result?.subscription?.neverExpires
            : false,
          planId: result.subscription ? result.subscription.planId : '',
        }))
        setAddonExtensions(result.additionalExtensions || 0)
        setAddonGroups(result.additionalGroups || 0)
      })
      .catch(() => {
        toast.error('Error loading organization', options)
      })
  }

  const loadPlans = () => {
    plansAndBundlesService
      .getPlans()
      .then((result) => {
        setState((prev) => ({
          ...prev,
          loading: false,
          plans: result,
        }))
      })
      .catch(() => {})
  }

  // const getPlanName = (planId) => {
  //   const plans = state.plans
  //   if (plans.length > 0) {
  //     const planName = plans.filter((element) => {
  //       return element.id === planId
  //     })
  //     return planName[0].name
  //   }
  // }

  const cancelPlan = () => {
    const stripe = isStripeSubscription(state.organization.subscription)
    confirm(
      stripe
        ? 'Turn off automatic billing? Your plan stays active until the end of the current period and is not renewed.'
        : 'Are you sure you want to cancel current plan?',
    ).then(
      () => {
        paymentService
          .cancelPurchase(state.organization.subscription.id, state.organization, null)
          .then(async () => {
            toast.success(
              stripe ? 'Automatic billing is turned off' : 'Package is successfully canceled!',
              options,
            )
            await loadOrganizationById()
          })
          .catch((error) => {
            toast.error(error?.response?.data?.error?.message || 'Error canceling plan', options)
          })
      },
      () => {},
    )
  }

  const resumePlan = () => {
    paymentService
      .resumePurchase(state.organization.subscription.id, state.organization)
      .then(async () => {
        toast.success('Automatic billing is turned on', options)
        await loadOrganizationById()
      })
      .catch((error) => {
        toast.error(
          error?.response?.data?.error?.message || 'Error turning on automatic billing',
          options,
        )
      })
  }

  const handleChange = (event) => {
    const target = event.target
    let organization = { ...state.organization }
    const value = target.type === 'checkbox' ? target.checked : target.value
    const name = target.name
    const planId = name === 'planId' ? value : state?.planId
    const selectedPlan = state?.plans?.find((plan) => plan?.planId === planId)
    let disableSubscribe = selectedPlan?.type === PLAN_TYPES.trial
    let subscribeObject = {
      subscribe: disableSubscribe ? false : name === 'subscribe' ? value : state.subscribe,
    }
    if (disableSubscribe && name === 'subscribe') {
      toastService.show('error', 'You are not able to subscribe to the trial plan')
    }
    // The free trial is only offered together with automatic billing.
    if (!subscribeObject.subscribe) setUseTrial(false)
    setState((prev) => ({
      ...prev,
      [name]: value,
      activeClass: value,
      error: null,
      organization: organization,
      ...subscribeObject,
    }))
  }

  const handleTrialChange = (event) => {
    const checked = event.target.checked
    const plan = state?.plans?.find((item) => item?.planId === state?.planId)
    if (checked && plan?.type === PLAN_TYPES.trial) {
      toastService.show('error', 'The free trial is not available for the trial plan')
      return
    }
    setUseTrial(checked)
    // A free trial always comes with automatic billing.
    if (checked) setState((prev) => ({ ...prev, subscribe: true }))
  }

  const isChecked = (planId) => {
    return state?.planId === planId
  }
  const handleShowModalMinut = () => {
    setState((prev) => ({
      ...prev,
      showModalMinut: true,
    }))
  }

  const handleClose = () => {
    if (state.showModalMinut) {
      setState((prev) => ({ ...prev, showModalMinut: false }))
    } else if (state.showModalPayment) {
      setState((prev) => ({ ...prev, showModalPayment: false }))
    }
  }

  const handleShowModalPayment = () => {
    if (isValid()) {
      setState((prev) => ({
        ...prev,
        showModalPayment: true,
      }))
    } else {
      setState((prev) => ({
        ...prev,
        error: 'You must select plan!',
      }))
    }
  }

  const isValid = () => {
    return state.planId
  }

  const getPlanByPlanId = (planId) => {
    return state?.plans?.find((plan) => plan.planId === planId)
  }

  const buildAddonConfirmMessage = () => {
    const currentExt = state.organization?.additionalExtensions ?? 0
    const currentGrp = state.organization?.additionalGroups ?? 0
    const extPrice = state.organization?.subscription?.extensionPrice ?? 0
    const grpPrice = state.organization?.subscription?.groupPrice ?? 0
    const lines = []
    const describe = (label, current, next, price) => {
      if (next === current) return
      const diff = next - current
      if (diff > 0) {
        lines.push(
          `Add ${diff} ${label}${
            diff > 1 ? 's' : ''
          } (now ${next}). This is billed immediately, prorated for the current period${
            price > 0 ? ` at $${price}/month each` : ''
          }.`,
        )
      } else {
        lines.push(
          `Remove ${Math.abs(diff)} ${label}${
            Math.abs(diff) > 1 ? 's' : ''
          } (now ${next}). The reduction applies from your next billing cycle.`,
        )
      }
    }
    describe('extension', currentExt, addonExtensions, extPrice)
    describe('group', currentGrp, addonGroups, grpPrice)
    if (!lines.length) return null
    return `You are about to update your add-on slots:\n\n${lines.join('\n\n')}`
  }

  const handleAddonUpdate = async () => {
    const message = buildAddonConfirmMessage()
    if (!message) {
      toast.error('No changes to save', options)
      return
    }
    confirm(message, { title: 'Confirm add-on changes' }).then(
      async () => {
        setAddonSaving(true)
        try {
          await paymentService.purchaseAddons(organization._id, addonExtensions, addonGroups)
          toast.success('Addon slots updated successfully', options)
          await loadOrganizationById()
        } catch {
          // error already shown by httpService interceptor
        } finally {
          setAddonSaving(false)
        }
      },
      () => {},
    )
  }

  const describeBundleUnit = (entry) =>
    entry.type === 'sms' ? `${entry.sms} texts` : `${entry.minutes} minutes`

  // One more unit of a monthly add-on: charged now and renewed with the plan.
  // It is bought at the bundle's current price, which can differ from the price
  // the add-on was first bought at.
  const addRecurringBundleUnit = async (entry) => {
    setBundleSaving(true)
    let bundle
    try {
      const bundles = await plansAndBundlesService.getBundles()
      bundle = (bundles || []).find((item) => item._id === entry.bundleId)
    } catch {
      // handled below
    } finally {
      setBundleSaving(false)
    }
    if (!isMonthlyBundle(bundle)) {
      toast.error('This bundle is no longer available as a monthly add-on', options)
      return
    }
    const renewal = getNextRenewalDate(state.organization.subscription)
    confirm(
      `Add one more ${describeBundleUnit(entry)} bundle to your monthly plan? $${
        bundle.price
      } is charged now with your saved card, then $${bundle.price}/month with your plan${
        renewal ? ` from ${renewal}` : ''
      }.`,
      { title: 'Add monthly add-on' },
    ).then(
      async () => {
        setBundleSaving(true)
        try {
          await paymentService.purchaseExtraMinutes(state.organization._id, null, bundle._id, true)
          toast.success('Monthly add-on updated', options)
          await loadOrganizationById()
        } catch {
          // error already shown by httpService interceptor
        } finally {
          setBundleSaving(false)
        }
      },
      () => {},
    )
  }

  // Fewer units or removal (quantity 0): applies from the next renewal, no refund
  const changeRecurringBundleQuantity = (entry, quantity) => {
    const renewal = getNextRenewalDate(state.organization.subscription) || 'the next renewal'
    const message =
      quantity === 0
        ? `Remove the ${describeBundleUnit(
            entry,
          )} bundle from your monthly plan? It is not renewed from ${renewal}. The minutes or texts you already have stay until then. Nothing is refunded.`
        : `Reduce the ${describeBundleUnit(
            entry,
          )} bundle to ${quantity} from ${renewal}? The minutes or texts you already have stay until then. Nothing is refunded.`
    confirm(message, {
      title: quantity === 0 ? 'Remove monthly add-on' : 'Reduce monthly add-on',
    }).then(
      async () => {
        setBundleSaving(true)
        try {
          await paymentService.updateRecurringBundle(state.organization._id, entry._id, quantity)
          toast.success('Monthly add-on updated', options)
          await loadOrganizationById()
        } catch {
          // error already shown by httpService interceptor
        } finally {
          setBundleSaving(false)
        }
      },
      () => {},
    )
  }

  const { organization, planId, plans, showModalMinut, showModalPayment, error, subscribe } = state

  if (state?.loading) {
    return (
      <div>
        <Loader />
      </div>
    )
  }

  const selectedPlan = getPlanByPlanId(planId)

  const isOnlyTrialPlan = plans?.filter((plan) => plan.type === PLAN_TYPES.trial) || []

  return (
    <div>
      {showModalMinut ? (
        <AddMinutes
          show={showModalMinut}
          handleClose={handleClose}
          organizationId={organization._id}
          subscription={organization.subscription}
          onPurchased={loadOrganizationById}
        />
      ) : null}
      {showModalPayment ? (
        <PlanPayment
          show={showModalPayment}
          handleClose={handleClose}
          planId={planId}
          plans={plans}
          organizationId={organization._id}
          subscribe={subscribe}
          useTrial={useTrial}
        />
      ) : null}

      <div className='curent-plan'>
        {organization.subscription ? (
          <Row className='page-header py-4'>
            <PageTitle
              title={`Current plan: ${organization.subscription.planName}`}
              subtitle='Subscription plan management'
              className='text-sm-left'
            />
            <Row className='curent-plan-options'>
              <Col sm='12' lg='6'>
                <div className='subtitle-border-bottom h-100'>
                  <ul>
                    <li>
                      <p>Selected organization</p> <span> {organization.name}</span>
                    </li>
                    <li>
                      <p>Automatic billing</p>{' '}
                      <span>{isAutoBillingOn(organization.subscription) ? 'ON' : 'OFF'}</span>
                    </li>
                    {!isSubscriptionActive(organization.subscription) ? (
                      <li>
                        <p>Minutes and texts</p>
                        <span>Not available: the subscription is not active</span>
                      </li>
                    ) : (
                      <>
                        <li>
                          <p>Available call credits</p>{' '}
                          <span>
                            {organization.limit !== 0
                              ? Math.floor(organization.limit / 60)
                              : organization.limit}
                          </span>
                        </li>
                        <li>
                          <p>Additional call credits</p>{' '}
                          <span>
                            {organization.extraLimit !== 0
                              ? Math.floor(organization.extraLimit / 60)
                              : organization.extraLimit}
                          </span>
                        </li>
                        <li>
                          <p>Available texts (SMS/MMS)</p> <span>{organization?.sms}</span>
                        </li>
                        <li>
                          <p>Additional texts</p>
                          <span>{organization?.extraSms}</span>
                        </li>
                        {organization?.carriedLimit > 0 || organization?.carriedSms > 0 ? (
                          <li>
                            <p>Carried over from last period</p>
                            <span>
                              {[
                                organization.carriedLimit > 0
                                  ? `${Math.floor(organization.carriedLimit / 60)} min`
                                  : null,
                                organization.carriedSms > 0
                                  ? `${organization.carriedSms} texts`
                                  : null,
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                              {getNextRenewalDate(organization.subscription)
                                ? `, valid until ${getNextRenewalDate(organization.subscription)}`
                                : ''}
                            </span>
                          </li>
                        ) : null}
                      </>
                    )}
                    {isStripeSubscription(organization.subscription) ? (
                      <li>
                        <p>
                          {getBillingDate(organization.subscription)?.label || 'Next charge on'}
                        </p>
                        <span>{getBillingDate(organization.subscription)?.date || 'N/A'}</span>
                      </li>
                    ) : (
                      <li>
                        <p>Expiration date </p>
                        <span>
                          {!organization.subscription.neverExpires
                            ? organization.subscription.firstBillingDate
                            : 'N/A'}
                        </span>
                      </li>
                    )}
                    <li>
                      <p>VoIP Number</p>{' '}
                      <span>{organization?.twilioNumber ? organization?.twilioNumber : 'N/A'}</span>
                    </li>
                  </ul>
                </div>
              </Col>
              <Col sm='12' lg='6'>
                <div className='curent-plan-right'>
                  {isStripeSubscription(organization.subscription) ? (
                    isAutoBillingOn(organization.subscription) ? (
                      <div className=''>
                        <button className='btn btn-lightblue' onClick={cancelPlan}>
                          Turn off automatic billing
                        </button>
                        <p>
                          Your plan stays active until the end of the current period and is not
                          renewed. Then remaining minutes, texts and extra bundles end and are not
                          carried over, monthly add-ons end with the plan, and your VoIP numbers are
                          released 7 days later.
                        </p>
                        <hr />
                      </div>
                    ) : (
                      <div className=''>
                        <button className='btn btn-lightblue' onClick={resumePlan}>
                          Turn automatic billing back on
                        </button>
                        <p>
                          Automatic billing is off: your plan ends on{' '}
                          {getBillingDate(organization.subscription)?.date ||
                            'the end of the period'}
                          . Turn it back on to keep your plan, minutes and VoIP numbers.
                        </p>
                        <hr />
                      </div>
                    )
                  ) : organization?.subscription?.neverExpires ? (
                    <div className=''>
                      <button className='btn btn-lightblue' onClick={cancelPlan}>
                        Cancel subscription
                      </button>
                      <p>
                        You can still use the same service until the expiration date. Then your VoIP
                        numbers are released 7 days later unless you subscribe again.
                      </p>
                      <hr />
                    </div>
                  ) : null}
                  <div className=''>
                    <button className='btn btn-lightblue' onClick={handleShowModalMinut}>
                      Purchase more voice minutes or texts (SMS/MMS)
                    </button>
                    <p>
                      Add more minutes or texts (SMS/MMS) to your plan, for the current period only
                      or every month together with your plan. Unused bundle minutes and texts carry
                      over to the next period once, then they expire.
                    </p>
                  </div>
                </div>
              </Col>
            </Row>
            {(() => {
              const recurringBundles = organization.recurringBundles || []
              const currentPlan = getPlanByPlanId(organization.subscription.planId)
              const totals = getRecurringBundleTotals(organization)
              const renewal = getNextRenewalDate(organization.subscription)
              const extPrice = organization.subscription.extensionPrice ?? 0
              const grpPrice = organization.subscription.groupPrice ?? 0
              const extensions =
                (organization.defaultExtensions ?? 0) + (organization.additionalExtensions ?? 0)
              const groups =
                (organization.defaultGroups ?? 0) + (organization.additionalGroups ?? 0)
              if (!currentPlan && !recurringBundles.length) return null
              const summary = currentPlan
                ? [
                    `${(currentPlan.minutes || 0) + totals.minutes} min`,
                    `${(currentPlan.sms || 0) + totals.sms} texts`,
                    extensions > 0 ? `${extensions} extension${extensions > 1 ? 's' : ''}` : null,
                    groups > 0 ? `${groups} group${groups > 1 ? 's' : ''}` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')
                : null
              const monthlyPrice = currentPlan
                ? (currentPlan.price || 0) +
                  (organization.additionalExtensions ?? 0) * extPrice +
                  (organization.additionalGroups ?? 0) * grpPrice +
                  totals.price
                : null
              return (
                <Row className='mt-4'>
                  <Col sm='12'>
                    <Card className='card-custom box-shadow-no border-gray'>
                      <CardBody>
                        <h5 className='mb-1'>Your monthly plan</h5>
                        {summary && (
                          <p className={styles.customPlanSummary}>
                            {summary}
                            {monthlyPrice !== null && (
                              <strong> — ${monthlyPrice.toFixed(2)}/month</strong>
                            )}
                          </p>
                        )}
                        <p className='text-muted small mb-3'>
                          Plan, extra extensions and groups, and the monthly add-ons below, billed
                          together{renewal ? ` on ${renewal}` : ''}. Removing an add-on applies from
                          the next renewal; nothing is refunded.
                        </p>
                        <h6 className='mb-2'>Monthly add-ons</h6>
                        {recurringBundles.length ? (
                          <ul className={styles.recurringList}>
                            {recurringBundles.map((entry) => {
                              const removed = entry.removeAtPeriodEnd || !(entry.quantity > 0)
                              return (
                                <li key={entry._id} className={styles.recurringItem}>
                                  <div>
                                    <span className={styles.recurringName}>
                                      {entry.name || describeBundleUnit(entry)}
                                    </span>
                                    <span className={styles.recurringDetails}>
                                      {describeBundleUnit(entry)} · ${entry.price}/month each
                                      {removed && renewal ? ` · ends on ${renewal}` : ''}
                                    </span>
                                  </div>
                                  {removed ? (
                                    <span className={styles.recurringEnds}>Removed</span>
                                  ) : (
                                    <div className={styles.addonCounter}>
                                      <button
                                        className={styles.addonCounterBtn}
                                        disabled={bundleSaving}
                                        onClick={() =>
                                          changeRecurringBundleQuantity(entry, entry.quantity - 1)
                                        }
                                      >
                                        −
                                      </button>
                                      <span className={styles.recurringQuantity}>
                                        {entry.quantity}
                                      </span>
                                      <button
                                        className={styles.addonCounterBtn}
                                        disabled={bundleSaving}
                                        onClick={() => addRecurringBundleUnit(entry)}
                                      >
                                        +
                                      </button>
                                      <button
                                        className='btn btn-link btn-sm'
                                        disabled={bundleSaving}
                                        onClick={() => changeRecurringBundleQuantity(entry, 0)}
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  )}
                                </li>
                              )
                            })}
                          </ul>
                        ) : (
                          <p className='light-grey small mb-0'>
                            No monthly add-ons yet. Use “Purchase more voice minutes or texts” and
                            choose “Add to my monthly plan”.
                          </p>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              )
            })()}
            <Row className='mt-4'>
              <Col sm='12'>
                <Card className='card-custom box-shadow-no border-gray'>
                  <CardBody>
                    <h5 className='mb-1'>Extensions &amp; Groups</h5>
                    <p className='text-muted small mb-3'>
                      Your plan includes a set number of extensions and groups. You can purchase
                      additional slots at any time. Increases are billed immediately (prorated),
                      decreases apply from your next billing cycle.
                    </p>
                    <Row>
                      <Col sm='12' lg='6' className='mb-4'>
                        {(() => {
                          const defaultExt = organization?.defaultExtensions ?? 0
                          const extPrice = organization?.subscription?.extensionPrice ?? 0
                          const totalSlots = defaultExt + (organization?.additionalExtensions ?? 0)
                          return (
                            <div className={styles.addonBox}>
                              <div className={styles.addonHeader}>
                                <span>Extensions</span>
                                <span className={styles.addonTotal}>{totalSlots} total slots</span>
                              </div>
                              <ul>
                                <li>
                                  <p>Default from plan</p>
                                  <span>{defaultExt}</span>
                                </li>
                                <li>
                                  <p>Additional purchased</p>
                                  <span>{organization?.additionalExtensions ?? 0}</span>
                                </li>
                                {extPrice > 0 && (
                                  <li>
                                    <p>Price per additional</p>
                                    <span>${extPrice}/month</span>
                                  </li>
                                )}
                                {extPrice > 0 && (
                                  <li>
                                    <p>Current additional cost</p>
                                    <span>
                                      $
                                      {(
                                        (organization?.additionalExtensions ?? 0) * extPrice
                                      ).toFixed(2)}
                                      /month
                                    </span>
                                  </li>
                                )}
                              </ul>
                              {extPrice > 0 && (
                                <div className={styles.addonControl}>
                                  <p className={styles.addonControlLabel}>Additional extensions</p>
                                  <div className={styles.addonCounter}>
                                    <button
                                      className={styles.addonCounterBtn}
                                      onClick={() =>
                                        setAddonExtensions(Math.max(0, addonExtensions - 1))
                                      }
                                    >
                                      −
                                    </button>
                                    <input
                                      type='number'
                                      min='0'
                                      className={styles.addonCounterInput}
                                      value={addonExtensions}
                                      onChange={(e) =>
                                        setAddonExtensions(
                                          Math.max(0, parseInt(e.target.value, 10) || 0),
                                        )
                                      }
                                    />
                                    <button
                                      className={styles.addonCounterBtn}
                                      onClick={() => setAddonExtensions(addonExtensions + 1)}
                                    >
                                      +
                                    </button>
                                  </div>
                                  <p className={styles.addonCostPreview}>
                                    New additional cost:{' '}
                                    <strong>
                                      ${(addonExtensions * extPrice).toFixed(2)}/month
                                    </strong>
                                  </p>
                                </div>
                              )}
                            </div>
                          )
                        })()}
                      </Col>
                      <Col sm='12' lg='6' className='mb-4'>
                        {(() => {
                          const defaultGrp = organization?.defaultGroups ?? 0
                          const grpPrice = organization?.subscription?.groupPrice ?? 0
                          const totalSlots = defaultGrp + (organization?.additionalGroups ?? 0)
                          return (
                            <div className={styles.addonBox}>
                              <div className={styles.addonHeader}>
                                <span>Groups</span>
                                <span className={styles.addonTotal}>{totalSlots} total slots</span>
                              </div>
                              <ul>
                                <li>
                                  <p>Default from plan</p>
                                  <span>{defaultGrp}</span>
                                </li>
                                <li>
                                  <p>Additional purchased</p>
                                  <span>{organization?.additionalGroups ?? 0}</span>
                                </li>
                                {grpPrice > 0 && (
                                  <li>
                                    <p>Price per additional</p>
                                    <span>${grpPrice}/month</span>
                                  </li>
                                )}
                                {grpPrice > 0 && (
                                  <li>
                                    <p>Current additional cost</p>
                                    <span>
                                      $
                                      {((organization?.additionalGroups ?? 0) * grpPrice).toFixed(
                                        2,
                                      )}
                                      /month
                                    </span>
                                  </li>
                                )}
                              </ul>
                              {grpPrice > 0 && (
                                <div className={styles.addonControl}>
                                  <p className={styles.addonControlLabel}>Additional groups</p>
                                  <div className={styles.addonCounter}>
                                    <button
                                      className={styles.addonCounterBtn}
                                      onClick={() => setAddonGroups(Math.max(0, addonGroups - 1))}
                                    >
                                      −
                                    </button>
                                    <input
                                      type='number'
                                      min='0'
                                      className={styles.addonCounterInput}
                                      value={addonGroups}
                                      onChange={(e) =>
                                        setAddonGroups(
                                          Math.max(0, parseInt(e.target.value, 10) || 0),
                                        )
                                      }
                                    />
                                    <button
                                      className={styles.addonCounterBtn}
                                      onClick={() => setAddonGroups(addonGroups + 1)}
                                    >
                                      +
                                    </button>
                                  </div>
                                  <p className={styles.addonCostPreview}>
                                    New additional cost:{' '}
                                    <strong>${(addonGroups * grpPrice).toFixed(2)}/month</strong>
                                  </p>
                                </div>
                              )}
                            </div>
                          )
                        })()}
                      </Col>
                    </Row>
                    {(organization?.subscription?.extensionPrice > 0 ||
                      organization?.subscription?.groupPrice > 0) && (
                      <div className={styles.addonSaveRow}>
                        <div>
                          <p className={styles.addonNewTotal}>
                            New monthly addon total:{' '}
                            <strong>
                              $
                              {(
                                addonExtensions *
                                  (organization?.subscription?.extensionPrice ?? 0) +
                                addonGroups * (organization?.subscription?.groupPrice ?? 0)
                              ).toFixed(2)}
                              /month
                            </strong>
                          </p>
                        </div>
                        <button
                          className='btn btn-lightblue'
                          onClick={handleAddonUpdate}
                          disabled={addonSaving}
                        >
                          {addonSaving ? 'Saving...' : 'Save changes'}
                        </button>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Row>
        ) : (
          <div>
            <Row className='page-header pt-4'>
              <PageTitle title='Purchase a plan' subtitle='Subscription' className='text-sm-left' />
              <Col sm='12' md='12' lg='8' xl='6'>
                <p className='m-0 light-grey'>
                  Associate a purchase plan with your organization. Purchased plan and minutes will
                  be applicable only for this organization.
                </p>
              </Col>
            </Row>
            <Stepper step={2} />
          </div>
        )}
      </div>

      {organization.subscription ? (
        <h3 className='mb-3'>Change your subscription plan for organization </h3>
      ) : null}
      {(!selectedPlan || selectedPlan?.type !== PLAN_TYPES.trial) &&
      isOnlyTrialPlan.length !== plans.length ? (
        <div className='form-organization'>
          <input
            className='custom-checkbox'
            type='checkbox'
            name='subscribe'
            id='subscribe'
            onChange={handleChange}
            checked={subscribe}
            value={subscribe}
            disabled={useTrial}
          />
          <label htmlFor='subscribe' className='m-0'>
            {' '}
            Automatic billing
          </label>
          <p className='small'>
            By selecting “Automatic Billing”, your selected subscription plan will automatically
            renew every 30 days.
          </p>
        </div>
      ) : null}
      {trialAvailable ? (
        <div className='form-organization'>
          <input
            className='custom-checkbox'
            type='checkbox'
            name='useTrial'
            id='useTrial'
            onChange={handleTrialChange}
            checked={useTrial}
          />
          <label htmlFor='useTrial' className='m-0'>
            {' '}
            Start with a free trial
          </label>
          <p className='small'>
            Enable your initial 30-day free trial. You won’t be charged until the trial ends. This
            offer can only be used once.
          </p>
          <p className='small'>
            Requires a valid credit card and automatic billing — you’ll be charged
            {selectedPlan?.price ? ` $${selectedPlan.price}` : ' for the selected plan'} on day 31
            unless you cancel before the trial ends.
          </p>
        </div>
      ) : null}
      {state.loading ? (
        <div className=' mb-5'>
          <Row>
            <Col sm='12' md='12' lg='8' xl='6'>
              <BeatLoader
                // css={override}
                // sizeUnit={'px'}
                size={15}
                color={'#299bff'}
                loading={state.loading}
              />
            </Col>
          </Row>
        </div>
      ) : null}
      {plans.map((plan, ind) => (
        <Row key={plan._id} className={styles.rowBox}>
          <Col sm='12' md='12' lg='8' xl='6' className={styles.paymentBox}>
            <Card
              className={
                state.activeClass === plan.id
                  ? `${'active-plan-card card-custom card-plan plan-cards-box box-shadow-no border-gray'} ${
                      styles.planBox
                    }`
                  : `${'card-custom card-plan plan-cards-box box-shadow-no border-gray'} ${
                      styles.planBox
                    }`
              }
            >
              <CardBody className={styles.cardBodyBox}>
                <div className='plan-card-box'>
                  <div className='plan-card-name'>
                    <span>{ind + 1}</span>
                  </div>
                  <div className='plan-card-desc'>
                    <div>
                      <h6>
                        {plan.name} <span className='lightblue'>{plan.price}$</span>
                      </h6>
                      <QuillReact value={plan.description || ''} />
                      {(() => {
                        const included = []
                        if (plan.defaultExtensions > 0) {
                          included.push(
                            `${plan.defaultExtensions} extension${
                              plan.defaultExtensions > 1 ? 's' : ''
                            }`,
                          )
                        }
                        if (plan.defaultGroups > 0) {
                          included.push(
                            `${plan.defaultGroups} group${plan.defaultGroups > 1 ? 's' : ''}`,
                          )
                        }
                        return (
                          <p className='light-grey small mt-2 mb-0'>
                            Includes an initial 30-day free trial
                            {included.length ? `, plus ${included.join(' and ')}` : ''}.
                          </p>
                        )
                      })()}
                    </div>
                    <div className='custom-radio-container'>
                      <input
                        id={`sel-${plan.planId}`}
                        type='radio'
                        value={plan.planId}
                        name='planId'
                        checked={isChecked(plan.planId)}
                        className='custom-radio'
                        onChange={handleChange}
                      />
                      <label htmlFor={`sel-${plan.planId}`}> </label>
                      <div className='check'>
                        <div className='inside'></div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
            {planId === plan.planId &&
            (isChecked(plan.planId) || !organization?.subscription?.planId) ? (
              <div className='text-center d-flex align-items-center pb-4'>
                <button
                  className={`${'btn btn-primary'} ${styles.prceedBtn} `}
                  onClick={handleShowModalPayment}
                >
                  Proceed to payment
                </button>
              </div>
            ) : null}
          </Col>
        </Row>
      ))}
      {error && <div className={'alert alert-danger'}>{error}</div>}
      {/* {!this.state.loading ?
          <Row >
            <Col sm="12" md="12" lg="8" xl="6">
              <div className="pull-right">
                <button className="btn btn-purple" onClick={this.handleShowModalPayment}>Proceed to payment</button>
              </div>
            </Col>
          </Row> : null} */}
    </div>
  )
}

export default Plan
